import { showNotification, updateNotification } from '@mantine/notifications';
import { ContactDescription } from '../Types/AuthT';
import { ContactsFilter } from '../Types/Contact';
import { DistanceDto } from '../Types/LocationT';
import { PageableResponse } from '../Types/PageableResponse';
import { RequestAnswer } from '../Types/RecommendationT';
import { toUTC } from '../Utils/dateUtils';
import { ajaxActions } from './AjaxActions';
import { EventS } from './EventS';
import { FileS } from './FileS';
import { ToastS } from './ToastS';
import { MixpanelS } from './MixpanelS';
import { ConstantS } from './ConstantS';
import { isDefined } from './ValidationS';

type ContactType = 'PRIVATE' | 'COMPANY';

export type Country =
  | 'AT' // Austria
  | 'BE' // Belgium
  | 'BG' // Bulgaria
  | 'CH' // Switzerland
  | 'CY' // Cyprus
  | 'CZ' // Czechia
  | 'DE' // Germany
  | 'DK' // Denmark
  | 'EE' // Estonia
  | 'ES' // Spain
  | 'FI' // Finland
  | 'FR' // France
  | 'GB' // United Kingdom
  | 'GR' // Greece
  | 'HR' // Croatia
  | 'HU' // Hungary
  | 'IE' // Ireland
  | 'IS' // Iceland
  | 'IT' // Italy
  | 'LI' // Liechtenstein
  | 'LT' // Lithuania
  | 'LU' // Luxembourg
  | 'LV' // Latvia
  | 'MT' // Malta
  | 'NL' // Netherlands
  | 'NO' // Norway
  | 'PL' // Poland
  | 'PT' // Portugal
  | 'RO' // Romania
  | 'SE' // Sweden
  | 'SI' // Slovenia
  | 'SK' // Slovakia
  | 'US'; // United States

export type Contact = {
  id: number;
  applicationUserId: number;
  salutation?: string;
  firstName: string;
  lastName: string;
  companyName: string;
  email: string | null;
  addressStreetAndNo?: string;
  zipCode?: string;
  city?: string;
  phoneNumber?: string;
  phoneNumberAlt?: string;
  contactType: ContactType;
  lastModified: Date;
  groups: string[];
  salutationOfficial?: string;
  salutationLovely?: string;
  address?: string;
  personDescription: string;
  value?: string;
  distance?: DistanceDto | null; // God forbid me for this solution
  requestAnswer?: RequestAnswer;
  partnerId?: number | null;
  partner?: Contact;
  buyerReference?: string | null;
  website: string | null;
  country: Country;
  birthday: Date | null;
  addressSupplement: string | null;
};

export interface ContactsColumnVisibility {
  showMail: boolean;
  showPhone: boolean;
  showGroups: boolean;
}

export type ContactRelation = {
  id: number;
  description: string;
  relatedContactId: number;
};

export type ContactPersonDescription = Pick<Contact, 'personDescription'>;

const ContactSkeleton = {
  contactType: 'PRIVATE',
  country: 'DE',
};

const BASE_URL = process.env.REACT_APP_AUTH_SERVICE_URL;

const fetchAll = async (): Promise<Contact[]> => {
  const res = await ajaxActions.get(`${BASE_URL}/contacts`);
  if (res.ok) {
    return res.json();
  }
  ToastS.generalError();
  return [];
};

const fetchAllWithFilter = async (
  filter: ContactsFilter,
  abortSignal: AbortSignal,
): Promise<PageableResponse<Contact> | null> => {
  const params = new URLSearchParams(filter as any);
  const res = await ajaxActions.get(`${BASE_URL}/contacts/all?${params.toString()}`, { signal: abortSignal });
  if (res.ok) {
    return res.json();
  }
  return null;
};

const fetchById = async (id: number, abortSignal?: AbortSignal): Promise<Contact | null> => {
  const res = await ajaxActions.get(`${BASE_URL}/contacts/${id}`, { signal: abortSignal });
  if (res.ok) {
    return res.json();
  }
  ToastS.generalError();
  return null;
};

const edit = async (id: number, patch: Partial<Contact>): Promise<Contact | null> => {
  const res = await ajaxActions.patch(`${BASE_URL}/contacts/${id}`, patch);
  if (res.ok) {
    return res.json();
  }
  ToastS.generalError();
  return null;
};

const saveOrUpdate = async (contact: Partial<Contact>) => {
  const res = await ajaxActions.exchange('POST', `${BASE_URL}/contacts${contact.id ? `/${contact.id}` : ''}`, contact);
  if (res.ok) {
    return res.json();
  }
  if (res.status === 409) {
    ToastS.error('email-confict', 'Der Kontakt mit der angegebenen E-Mail-Adresse existiert bereits.');
    return null;
  }
  ToastS.generalError();
  return null;
};

const fetchRelatedContacts = async (contactId: number): Promise<ContactRelation[]> => {
  const res = await ajaxActions.get(`${BASE_URL}/relatedContacts/${contactId}`);
  if (res.ok) {
    return res.json();
  }
  ToastS.generalError();
  return [];
};

const addRelatedContact = async (contactId: number, relatedContactId: number): Promise<ContactRelation | null> => {
  const res = await ajaxActions.put(`${BASE_URL}/contactRelations/${contactId}/${relatedContactId}`);
  if (res.ok) {
    return res.json();
  }
  ToastS.generalError();
  return null;
};

const patchContactRelation = async (contactRelationId: number, patch: Partial<ContactRelation>) => {
  const res = await ajaxActions.patch(`${BASE_URL}/contactRelations/${contactRelationId}`, patch);
  if (res.ok) {
    return res.json();
  }
  ToastS.generalError();
  return null;
};

const deleteContactRelation = async (contactRelationId: number): Promise<boolean> => {
  const res = await ajaxActions.del(`${BASE_URL}/contactRelations/${contactRelationId}`);
  if (res.ok) {
    return true;
  }
  ToastS.generalError();
  return false;
};

const fetchDescriptions = async (
  contactIds: number[],
  abortSignal?: AbortSignal,
): Promise<ContactDescription | undefined> => {
  const res = await ajaxActions.get(`${BASE_URL}/contacts/description?contactIds=${[contactIds]}`, {
    signal: abortSignal,
  });
  if (res.ok) {
    return res.json();
  }
  ToastS.generalError();
  return undefined;
};

const getPartnerFormValues = (partner: Contact | undefined) => {
  if (!partner) {
    return {
      partnerSalutation: '',
      partnerFirstName: '',
      partnerLastName: '',
      partnerBirthday: undefined,
      partnerEmail: '',
      partnerPhoneNumber: '',
      partnerPhoneNumberAlt: '',
      partnerContactType: 'PRIVATE',
    };
  }
  const {
    salutation: partnerSalutation,
    firstName: partnerFirstName,
    lastName: partnerLastName,
    email: partnerEmail,
    phoneNumber: partnerPhoneNumber,
    phoneNumberAlt: partnerPhoneNumberAlt,
    birthday,
  } = partner;

  return {
    partnerSalutation: partnerSalutation ?? '',
    partnerFirstName,
    partnerLastName,
    partnerBirthday: birthday ? new Date(birthday) : undefined,
    partnerEmail,
    partnerPhoneNumber,
    partnerPhoneNumberAlt,
    partnerContactType: 'PRIVATE',
  };
};

const extractPartnerFormValues = (values: any, partner?: Contact): Partial<Contact> => {
  const partnerFields: Partial<Contact> = {
    salutation: values.partnerSalutation,
    firstName: values.partnerFirstName,
    lastName: values.partnerLastName,
    email: values.partnerEmail !== null && values.partnerEmail.trim() === '' ? null : values.partnerEmail,
    phoneNumber: values.partnerPhoneNumber,
    phoneNumberAlt: values.partnerPhoneNumberAlt,
    contactType: values.partnerContactType as ContactType,
    birthday: values.partnerBirthday ? toUTC(values.partnerBirthday) : null,
  };
  if (partner) {
    partnerFields.id = partner.id;
  }
  return partnerFields;
};

const getEventContacts = async (eventId: string): Promise<Contact[]> => {
  const participants = await EventS.getEventParticipations(eventId);
  const contactIds = participants.map((p) => p.contactId);
  return fetchContactsById(contactIds);
};

const fetchContactsById = async (contactIds: number[]): Promise<Contact[]> => {
  const res = await ajaxActions.post(`${BASE_URL}/contacts/list`, contactIds);
  if (res.ok) {
    return res.json();
  }
  return [];
};

const findByEmails = async (emailsOfInterests: string[]): Promise<number[]> => {
  const res = await ajaxActions.post(`${BASE_URL}/contacts/findByEmails`, emailsOfInterests);
  if (res.ok) {
    return res.json();
  }
  ToastS.generalError();
  return [];
};

const importContactsCSV = async (csvFile: File) => {
  const res = await ajaxActions.uploadPutFile(`${BASE_URL}/import/contacts`, csvFile);
  if (res.ok) {
    ToastS.success('import-event', 'Import finished');
  } else {
    ToastS.generalError();
  }
};

const fetchAllParticipants = async (ids: number[]): Promise<Contact[]> => {
  const res = await ajaxActions.get(`${BASE_URL}/contacts/multiple?contactIds=${ids}`);
  if (res.ok) {
    return res.json();
  }
  return [];
};

const getDescription = (contact: Contact) => {
  const { personDescription, partner } = contact;
  if (partner) {
    const { personDescription: partnerDescription } = partner;
    return `${personDescription} & ${partnerDescription}`;
  }
  return personDescription;
};

const exportContactsCSV = async (filename: string, filter: ContactsFilter) => {
  showNotification({
    id: 'export-contacts-csv',
    loading: true,
    title: 'Kontakte exportieren',
    message: 'Datei wird generiert ...',
    autoClose: false,
    disallowClose: false,
  });
  const { query, groupsOfInterest } = filter;
  let targetUrl = `${BASE_URL}/import/contacts?selectedGroups=${groupsOfInterest}`;
  if (query) {
    targetUrl += `&query=${query}`;
  }
  const res = await ajaxActions.get(targetUrl);
  if (res.ok) {
    MixpanelS.track(ConstantS.TrackingEvents.ContactsExported);
    const blob = await res.blob();
    FileS.download(filename, blob);
    updateNotification({
      id: 'export-contacts-csv',
      loading: false,
      message: 'Export abgeschlossen.',
      color: 'green',
      autoClose: 5000,
    });
  } else {
    updateNotification({
      id: 'export-contacts-csv',
      loading: false,
      message: 'Upps etwas ist schief gelaufen 🙁 Bitte versuchen Sie es später.',
      color: 'red',
      autoClose: 2000,
    });
  }
};

const matchQuery = (contact: Contact, eventName: string | null, query: string) => {
  const { personDescription, partner } = contact;
  let concated = `${personDescription}`;
  if (partner) {
    concated += `${partner.firstName}${partner.lastName}`;
  }
  if (isDefined(eventName)) {
    concated += eventName;
  }
  return concated.toLocaleLowerCase().includes(query.toLocaleLowerCase());
};

// Used by place autocomplete
const resolveCountry = (value: string): string => {
  if (['DE', 'AT', 'IT', 'CH'].includes(value)) {
    return value;
  }
  // Country wasn't recognize by ENUM --> return default
  return 'DE';
};

export const ContactS = {
  ContactSkeleton,
  fetchAll,
  fetchAllWithFilter,
  fetchById,
  edit,
  saveOrUpdate,
  fetchRelatedContacts,
  addRelatedContact,
  patchContactRelation,
  deleteContactRelation,
  fetchDescriptions,
  getPartnerFormValues,
  extractPartnerFormValues,
  getEventContacts,
  fetchContactsById,
  findByEmails,
  importContactsCSV,
  getDescription,
  fetchAllParticipants,
  matchQuery,
  exportContactsCSV,
  resolveCountry,
};
